import PropTypes from 'prop-types';
import _ from 'lodash';
import React, { Component } from 'react';
import { Row, Button, Popover, Radio, Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { withTranslation } from 'react-i18next';

import Icon from '../../../common/UI/Icon';
import ButtonClose from '../../../common/UI/ButtonClose';
import DebouncedInput from '../../../common/DebouncedInput';
import FilterList from '../../../Filters';
import { connect } from '../../../StateProvider';
import importActions from '../../../../actions/importActions';
import FIELD_TYPES from '../../../../configs/fieldTypes';

import styles from '../ImportStyles.less';

const ID_VALUE = '$ID';

class ImportHeader extends Component {
  static propTypes = {
    catalogId: PropTypes.string,
    filterFields: ImmutablePropTypes.list,
    filters: ImmutablePropTypes.map,
    closeModal: PropTypes.func,
    loading: PropTypes.bool,
    savingMessages: ImmutablePropTypes.map,
  };

  _setImportFilters = _.debounce((...args) => importActions.setImportFilters(...args));

  _setImportSearchText = _.debounce((...args) => importActions.setImportSearchText(...args));

  setSearchText = (searchText) => {
    const { loading } = this.props;
    if (loading) return;
    this._setImportSearchText(searchText);
  };

  setFilters = (filterId, fieldId, value) => {
    const { loading } = this.props;
    if (loading) return;
    this._setImportFilters(filterId, fieldId, value);
  };

  onChangeMode = (e) => {
    importActions.setImportMode(e.target.value);
  };

  onChangeMatchColumn = (columnId) => {
    importActions.setMatchColumn(columnId || null);
  };

  onChangeMatchField = (value) => {
    if (value === ID_VALUE) {
      importActions.setMatchField(null, true);
    } else {
      importActions.setMatchField(value || null, false);
    }
  };

  getColumnOptions = () => {
    const { columns } = this.props;
    if (!columns) return [];

    return columns
      .filter((c) => !c.get('static'))
      .map((c) => ({ value: c.get('id'), label: c.get('name') }))
      .toArray();
  };

  getFieldOptions = () => {
    const { catalogs, catalogId } = this.props;
    const fields = catalogs && catalogs.getIn([catalogId, 'fields']);
    const options = [{ value: ID_VALUE, label: this.props.t('import.match.idOption') }];

    if (fields) {
      fields.forEach((f) => {
        const type = f.get('type');
        if (type === FIELD_TYPES.TEXT || type === FIELD_TYPES.NUMBER) {
          options.push({ value: f.get('id'), label: f.get('name') });
        }
      });
    }

    return options;
  };

  renderModePopover() {
    const { importMode, matchColumnId, matchFieldId, matchIsId, t } = this.props;
    const mode = importMode || 'create';
    const popupContainer = () => document.getElementById('importTable');

    return (
      <div className={styles.importOptions}>
        <div className={styles.popoverContent}>
          <label className={styles.controlLabel}>{t('import.match.modeLabel')}</label>
          <Radio.Group className={styles.modeRadioGroup} value={mode} onChange={this.onChangeMode}>
            <Radio value="create">{t('import.modes.create')}</Radio>
            <Radio value="update">{t('import.modes.update')}</Radio>
            <Radio value="upsert">{t('import.modes.upsert')}</Radio>
          </Radio.Group>
        </div>

        {mode !== 'create' && (
          <div>
            <div className={styles.matchPair}>
              <div className={styles.matchPairCol}>
                <span className={styles.matchPairLabel}>{t('import.match.columnLabel')}</span>
                <Select
                  className={styles.controlSelect}
                  placeholder={t('import.match.columnPlaceholder')}
                  value={matchColumnId || undefined}
                  onChange={this.onChangeMatchColumn}
                  options={this.getColumnOptions()}
                  getPopupContainer={popupContainer}
                />
              </div>
              <span className={styles.matchPairSep}>=</span>
              <div className={styles.matchPairCol}>
                <span className={styles.matchPairLabel}>{t('import.match.fieldLabel')}</span>
                <Select
                  className={styles.controlSelect}
                  placeholder={t('import.match.fieldPlaceholder')}
                  value={matchIsId ? ID_VALUE : matchFieldId || undefined}
                  onChange={this.onChangeMatchField}
                  options={this.getFieldOptions()}
                  getPopupContainer={popupContainer}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  getFiltersCount() {
    const { queryParams } = this.props;
    if (!queryParams || !queryParams.reduce) return 0;

    return queryParams.reduce((count, byField) => {
      const active =
        byField &&
        byField.some &&
        byField.some((f) => {
          const value = f && f.get && f.get('value');
          return value && (value.size ? value.size > 0 : !!value);
        });
      return active ? count + 1 : count;
    }, 0);
  }

  render() {
    const { catalogId, filterFields, filters, closeModal, importMode, t } = this.props;
    const mode = importMode || 'create';
    const filtersCount = this.getFiltersCount();

    return (
      <div>
        <Row type="flex" justify="space-between" className={styles.modalHeader}>
          <div className={styles.headerMainPart}>
            <h2 className={styles.headerTitle}>{t('import.header')}</h2>
          </div>
          <ButtonClose key="close" large shiftRight onClick={closeModal} />
        </Row>

        <Row type="flex" className={styles.modalProperties}>
          <Popover
            placement="bottomLeft"
            trigger="click"
            overlayClassName={styles.modePopover}
            getPopupContainer={() => document.getElementById('importTable')}
            content={this.renderModePopover()}
          >
            <span className={styles.property}>
              <Button type="text" className={styles.toolbarButton}>
                <Icon className={styles.toolbarIcon} type="icon transfers-5" />
                <span>
                  {t('import.match.modeLabel')}: <b className={styles.toolbarValue}>{t(`import.modes.${mode}`)}</b>
                </span>
                <DownOutlined className={styles.toolbarCaret} />
              </Button>
            </span>
          </Popover>

          <Popover
            placement="bottomLeft"
            trigger="click"
            getPopupContainer={() => document.getElementById('importTable')}
            content={
              <FilterList
                catalogId={catalogId}
                fields={filterFields}
                filters={filters}
                className={styles.filterList}
                onSave={this.setFilters}
              />
            }
          >
            <span className={styles.property}>
              <Button type="text" key="filters" className={styles.toolbarButton} title={t('views.filters')}>
                <Icon className={styles.toolbarIcon} type="icon content-47" />
                <span>{t('filter.header')}</span>
                {filtersCount > 0 ? <b className={styles.filtersCount}>{filtersCount}</b> : null}
              </Button>
            </span>
          </Popover>

          <DebouncedInput
            key="search"
            placeholder={t('catalogData.quickSearch')}
            className={styles.search}
            onSave={this.setSearchText}
          />
        </Row>
      </div>
    );
  }
}

export default connect(withTranslation()(ImportHeader), {
  filterFields: ['import', 'filterFields'],
  queryParams: ['import', 'queryParams', 'filters'],
  loading: ['import', 'loading'],
  columns: ['import', 'columns'],
  catalogs: ['catalogs'],
  matchColumnId: ['import', 'matchColumnId'],
  matchFieldId: ['import', 'matchFieldId'],
  matchIsId: ['import', 'matchIsId'],
  importMode: ['import', 'importMode'],
});
